/**
 * One reducer per event name — SPEC_5 §4.3.
 *
 * Almost every event does one thing: it says which payloads to re-read, and the GET stays the one
 * place a sentence or a status is computed. Two are applied directly, because applying them IS
 * the point: `heartbeat` (the header's reading) and a run's own events (the run viewer, where
 * `thinking_delta` and `text_delta` are written AT their offset — stream/offsets.ts).
 *
 * **Replay is idempotent.** Nothing below increments or derives a number the server already sent:
 * deltas land at their offsets and every other run event is keyed by its `seq`. That is the whole
 * reason a reconnect with an honoured `Last-Event-ID` draws the same run as an uninterrupted one.
 */

import { useCompetition } from "../store/competition";
import type { Heartbeat, RunEvent, RunEventType } from "../api/types/competition";

/** The kinds a run writes. Each is forwarded to the run viewer with its `seq`. */
const RUN_EVENTS: readonly RunEventType[] = [
  "run_start",
  "preamble",
  "node_start",
  "thinking_delta",
  "text_delta",
  "tool_start",
  "tool_end",
  "skill",
  "refusal",
  "node_end",
  "resting",
  "run_end",
  "error",
];

/** Every event the server names: SPEC_4's `utils/events.Hub`. EventSource needs one listener each. */
export const NAMES = [
  "heartbeat",
  "resync",
  "datapack",
  "run",
  "build",
  "memory",
  "action",
  ...RUN_EVENTS,
] as const;

export type EventName = (typeof NAMES)[number];

/** One event, applied. `data` is its parsed payload. */
export function apply(name: EventName, data: Record<string, unknown>): void {
  const store = useCompetition.getState();
  switch (name) {
    case "heartbeat":
      store.heartbeatArrived(data as unknown as Heartbeat);
      return;

    case "resync":
      // The id is from another boot or has fallen off the 30-minute ring. Everything re-reads
      // over GET; the collections are the record.
      void store.loadAll();
      return;

    case "datapack":
      // A new DataPack changes the day and the official portfolio both.
      void store.load("today");
      void store.load("portfolio");
      return;

    case "run":
      // A run row moved: started at 05:30, a new attempt, ready, failed, superseded. Today says
      // which, in the server's words; a new run id there brings its events along.
      void store.load("today");
      return;

    case "build":
    case "memory":
    case "action":
      // Not on this page: the jobs ride on the heartbeat, a memory is the agent's, and an action
      // is a human act whose effect arrives as its own `datapack` or `run`.
      return;

    default: {
      const { session_id, seq, at, ...rest } = data;
      store.runEvent({
        session_id: typeof session_id === "string" ? session_id : null,
        seq: typeof seq === "number" ? seq : -1,
        type: name,
        at: typeof at === "string" ? at : "",
        data: rest,
      } as RunEvent & { session_id: string | null });
    }
  }
}
